import type { SaveUserAccountRequest, UserProfileData } from '@/services/api/auth/types';

/**
 * Validates if a string is a valid email format
 * @param email - The email string to validate
 * @returns boolean indicating if the email is valid
 */
const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  // Check for basic email format and exclude cognito default values
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isCognitoDefault = email.includes('cognito:') || email === 'cognito:default_val';
  
  return emailRegex.test(email) && !isCognitoDefault;
};

/**
 * Interface for JWT user data
 */
interface JWTUserData {
  profile: {
    sub: string;
    email?: string;
    email_verified?: boolean;
    given_name?: string;
    family_name?: string;
    preferred_username?: string;
    'cognito:groups'?: string[];
    'cognito:username'?: string;
    'custom:external_user_id'?: string;
    'custom:mo_id'?: string;
    exp: number;
    iat: number;
    token_use: 'id' | 'access';
    aud?: string;
    iss?: string;
    name?: string;
    currency?: string;
    region?: string;
    moneta_id_token?: string;
  };
  access_token: string;
}

/**
 * Extracts user account data from JWT user data
 * @param userData - JWT user data object
 * @returns Formatted user account request data
 */
export const extractUserAccountData = (userData: JWTUserData): SaveUserAccountRequest => {
  if (!userData || !userData.profile) {
    throw new Error('User data object is required');
  }

  const profile = userData.profile;
  const email = profile.email;
  const validEmail = email && isValidEmail(email) ? email : undefined;

  // Prepare profile data based on the API sample structure
  const profileData: UserProfileData = {
    sub: profile.sub,
    currency: profile.currency,
    region: profile.region,
    name: profile.name || profile.given_name || profile["cognito:username"] || validEmail,
  };

  // Only include email if it's valid
  if (validEmail) {
    profileData.email = validEmail;
  }

  // Add moneta token if available
  if (profile.moneta_id_token) {
    profileData.moneta_id_token = profile.moneta_id_token;
  }

  // Prepare the request payload
  const payload = {
    profile_data: profileData,
    is_active: true,
  } as SaveUserAccountRequest;

  // Only include email in the payload if it's valid
  if (validEmail) {
    payload.email = validEmail;
  }

  return payload;
};

/**
 * Validates required user data for account creation
 * @param userData - JWT user data object
 * @returns boolean indicating if user data is valid
 */
export const validateUserData = (userData: JWTUserData): boolean => {
  return !!(userData && userData.profile && userData.profile.sub);
};
